import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { HlmBtnDirective, HlmIconComponent } from '../../ui';

/**
 * Caps a long list to a scrollable viewport (~10 rows) and offers a one-time
 * "Show all N without scrollbar" button centered BELOW it. Once expanded the
 * list stays fully expanded — there is no re-collapse (per EB-3856 spec).
 * Projects the data rows only; keep any column header outside so it stays
 * fixed above the scroll area.
 */
@Component({
  selector: 'cmt-capped-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HlmBtnDirective, HlmIconComponent],
  template: `
    <div
      [class]="
        capped()
          ? (maxHeightRem() === null
              ? 'max-h-[28rem] overflow-y-auto rounded-lg'
              : 'overflow-y-auto rounded-lg')
          : ''
      "
      [style.max-height.rem]="capped() && maxHeightRem() !== null ? maxHeightRem() : null"
    >
      <ng-content />
    </div>
    @if (capped()) {
      <div class="mt-3 flex justify-center">
        <button hlmBtn variant="outline" size="sm" class="h-8" (click)="expand()">
          <hlm-icon name="lucideChevronsDown" size="0.85rem" /> Show all {{ count() }} without scrollbar
        </button>
      </div>
    }
  `,
})
export class CappedListComponent {
  /** Total number of rows projected. */
  readonly count = input<number>(0);
  /** Rows beyond this stay behind the scroll + one-time expand. */
  readonly threshold = input<number>(10);
  /**
   * Optional explicit max-height (rem) for the capped scroll viewport. When
   * null (default) the viewport uses the standard `max-h-[28rem]` (~10 rows).
   * Set this when a caller wants a different number of visible rows.
   */
  readonly maxHeightRem = input<number | null>(null);

  private readonly expanded = signal(false);
  readonly capped = computed(() => !this.expanded() && this.count() > this.threshold());

  expand(): void {
    this.expanded.set(true);
  }
}
